import { AfterViewInit, Directive, ElementRef, HostListener, Input, OnDestroy, OnInit, Optional, Renderer2 } from '@angular/core';
import { FormControl, NgControl, NgModel, Validators } from '@angular/forms';
import { Subscription } from 'rxjs';
import { AvanoFormService } from '../../common/avano-form.service';

@Directive({
  selector: '[avanoInputTextarea]'
})
export class InputTextareaDirective implements OnInit, AfterViewInit, OnDestroy {
  @Input()
  formControl: FormControl;

  @Input()
  tooltip: string;

  @Input() set value(val: any) {
    // We intercept the default setting of "value", so we need to forward it!
    this.el.nativeElement.value = val;
    this.resize(); // That is the reason we intercept
  }

  // A "textarea" (textfield) can be resizable up to 7 lines of text
  // See "expanding text field" in AVANO
  @Input() isExpandingTextField: boolean;

  // If you want to generate an avano-compliant label, use this here
  // It's a setter to support dynamic changes of label
  @Input() set label(label: string) {
    this._label = label;
    this.handleLabel();
  }

  private _label: string;

  /** The gloabl container-element around the container (input+clear)
   * and the label and the x/y characters  */
  private generalContainer: HTMLDivElement;

  /** The container-element around the input and the clear-icon */
  private containerElement: HTMLSpanElement;

  /** The clear-icon for clearing the textarea */
  private clearIconSpan: HTMLSpanElement;
  private clearIcon: HTMLElement;

  /** Underneath a textarea/expanding textfield there is a "x/y"-characters count
   * Set "maxlength" to automatically render that */
  private lengthElement: HTMLDivElement;

  private labelElement: HTMLLabelElement;

  private ngModelSubscription: Subscription;

  private ngControlSubscription: Subscription;

  constructor(
    public el: ElementRef<HTMLTextAreaElement>,
    public renderer: Renderer2,
    public avanoFormService: AvanoFormService,
    @Optional() public ngModel: NgModel,
    @Optional() public control: NgControl
  ) {}

  ngOnInit() {
    this.generalContainer = document.createElement('div');
    this.renderer.addClass(this.generalContainer, 'avano-input-textarea-container');

    this.renderer.addClass(this.el.nativeElement, 'avano-input-textarea');

    if (this.isExpandingTextField) {
      this.el.nativeElement.rows = 1;
    }

    if (this.ngModel) {
      this.ngModelSubscription = this.ngModel.valueChanges.subscribe(() => {
        this.setMaxLengthContent();
        this.resize();
      });
    }

    if (this.control) {
      this.ngControlSubscription = this.control.valueChanges.subscribe(() => {
        this.setMaxLengthContent();
        this.resize();
      });
    }

    const nativeMaxLengthEnabled = this.el.nativeElement.maxLength && this.el.nativeElement.maxLength > 0;
    // TODO: Find a way to get "maxLength" from a formControl-validator :(
    if (nativeMaxLengthEnabled) {
      this.lengthElement = document.createElement('div');
      this.renderer.addClass(this.lengthElement, 'avano-input-textarea-maxlength');
      this.setMaxLengthContent();
    }
  }

  ngAfterViewInit(): void {
    this.createClearIconWithTextAreaContainerElement();

    // Insert the general container before the input element
    this.el.nativeElement.parentElement.insertBefore(this.generalContainer, this.el.nativeElement);

    if (this.labelElement) {
      this.generalContainer.appendChild(this.labelElement);
      this.handleLabel();
    }

    // Put the input element and clearicon inside the container.
    this.containerElement.appendChild(this.el.nativeElement);
    this.containerElement.appendChild(this.clearIconSpan);
    this.generalContainer.appendChild(this.containerElement);

    if (this.lengthElement) {
      this.generalContainer.appendChild(this.lengthElement);
    }

    this.resize();
  }

  ngOnDestroy() {
    if (this.ngModelSubscription) {
      this.ngModelSubscription.unsubscribe();
    }

    if (this.ngControlSubscription) {
      this.ngControlSubscription.unsubscribe();
    }
  }

  private setMaxLengthContent(): void {
    if (this.lengthElement) {
      this.lengthElement.innerText = this.el.nativeElement.value.length + '/' + this.el.nativeElement.maxLength;
    }
  }

  @HostListener('keyup')
  onKeyUp(): void {
    if (this.el.nativeElement.value !== '') {
      this.renderer.removeClass(this.containerElement, 'clear-input-empty');
    } else {
      this.renderer.addClass(this.containerElement, 'clear-input-empty');
    }
  }

  @HostListener('focus')
  onFocus(): void {
    this.renderer.addClass(this.containerElement, 'clear-input-active');
    this.resize();
  }

  @HostListener('blur')
  onBlur(): void {
    this.renderer.removeClass(this.containerElement, 'clear-input-active');
    if (this.isInputEmpty()) {
      this.renderer.addClass(this.containerElement, 'clear-input-empty');
    }
    this.resize();
  }

  @HostListener('mouseenter')
  onMouseEnter(): void {
    this.renderer.addClass(this.containerElement, 'clear-input-hover');
    this.setStyleClassForInput();
  }

  @HostListener('mouseleave', ['$event'])
  onMouseOut(event: any): void {
    // We do not want to remove our styleclasses if the user is hovering the child (icon-container/clear-icon)
    if (event.relatedTarget !== this.clearIcon && event.relatedTarget !== this.clearIconSpan) {
      this.renderer.removeClass(this.containerElement, 'clear-input-hover');
      this.setStyleClassForInput();
    }
  }

  @HostListener('input', ['$event.target', '$event.target.value'])
  onInput(e) {
    this.resize();
  }

  private setStyleClassForInput() {
    if (this.isInputEmpty()) {
      this.renderer.addClass(this.containerElement, 'clear-input-empty');
    } else {
      this.renderer.removeClass(this.containerElement, 'clear-input-empty');
    }
  }

  private isInputEmpty(): boolean {
    return this.formControl ? !this.formControl.value : !this.el.nativeElement.value?.length;
  }

  private onClearButtonClick(): void {
    // TODO: Remove formcontrol? Because dispatching it on the underplying input/textarea
    // should automatically trigger formControl too?
    if (this.formControl) {
      this.formControl.markAsDirty();
      this.formControl.setValue('');
    } else {
      this.renderer.addClass(this.containerElement, 'clear-input-empty');
      this.renderer.setProperty(this.el.nativeElement, 'value', '');
      this.el.nativeElement.dispatchEvent(new CustomEvent('input', { bubbles: true }));
    }
    this.resize(); // We know the input was cleared, so high likely the textarea should shrink (if autoresized)
  }

  private createClearIconWithTextAreaContainerElement(): void {
    this.containerElement = document.createElement('span');
    this.renderer.addClass(this.containerElement, 'input-textarea-container');
    this.renderer.addClass(this.containerElement, 'clear-input-empty');

    if (this.isReadOnly()) {
      this.renderer.addClass(this.containerElement, 'clear-input-readonly');
    }

    // We still want to create the clearicon already, because the "readonly" can change via an event (e.g. button bar click "Edit mode")
    this.createClearIcon();
  }

  private isReadOnly(): boolean {
    return this.el.nativeElement.readOnly;
  }

  private createClearIcon(): void {
    this.clearIconSpan = document.createElement('span');
    this.renderer.addClass(this.clearIconSpan, 'clear-input-button');
    this.clearIcon = document.createElement('i');
    this.renderer.addClass(this.clearIcon, 'icon-content-cancel');

    this.clearIconSpan.appendChild(this.clearIcon);
    // Add the event listener for the mousedown event. The click event does not work because blur triggers before click.
    this.clearIconSpan.addEventListener('mousedown', () => this.onClearButtonClick());
  }

  private resize() {
    if (this.isExpandingTextField) {
      this.el.nativeElement.style.height = 'auto';
      this.el.nativeElement.style.height = this.el.nativeElement.scrollHeight + 'px';
      if (parseFloat(this.el.nativeElement.style.height) >= 120) {
        this.el.nativeElement.style.overflowY = 'scroll';
        this.el.nativeElement.style.height = this.el.nativeElement.style.maxHeight;
        this.el.nativeElement.rows = 7;
      } else {
        // If the user is deleting his text we need to go back to the "hidden" to hide
        // the scrollbar until he again reaches the 7 lines input
        // We need to hide it because there are already some pixel overflow within a textarea
        this.el.nativeElement.style.overflow = 'hidden';
        this.el.nativeElement.rows = 1;
      }
    }
  }

  private handleLabel(): void {
    if (!this.labelElement && this._label) {
      this.labelElement = this.avanoFormService.generateFormLabelElement();
    }
    if (this.labelElement && this._label) {
      this.renderer.setProperty(this.labelElement, 'innerHTML', this._label);

      if (this.el.nativeElement.required || this.formControl?.hasValidator(Validators.required)) {
        this.renderer.appendChild(this.labelElement, this.avanoFormService.generateRequiredElement());
      }

      if (this.tooltip) {
        this.renderer.appendChild(this.labelElement, this.avanoFormService.generateTooltipElement(this.tooltip));
      }
    }
  }
}
